/**
 * Renders a `template_proposal` view: the proposed blocks as a tree (kind
 * icon, title, detail, a link for a referenced building block or product),
 * its assumptions, and a button that confirms the proposal with one click.
 */
import {
  Alert,
  Box,
  Button,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import type { AgentViewComponentProps } from "../registry.js";
import { TemplateViewFrame } from "./TemplateViewFrame.js";
import {
  TEMPLATE_NODE_ICON,
  useNodeDetailListFormatter,
  useTemplateNodeKindLabel,
} from "./templateNodeDisplay.js";
import {
  templateProposalViewSchema,
  type NodeDetail,
  type ProposalNode,
  type TemplateNodeKind,
  type TemplateProposalView,
} from "./templateViewTypes.js";

function ProposalTree({
  nodes,
  depth,
  kindLabel,
  formatDetail,
}: {
  nodes: ProposalNode[];
  depth: number;
  kindLabel: (kind: TemplateNodeKind) => string;
  formatDetail: (details: NodeDetail[]) => string;
}) {
  return (
    <Stack spacing={0.5}>
      {nodes.map((node, index) => (
        <Box key={index} sx={{ pl: depth * 2 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <Tooltip title={kindLabel(node.kind)}>
              <Box sx={{ pt: 0.25 }}>{TEMPLATE_NODE_ICON[node.kind]}</Box>
            </Tooltip>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: node.children.length > 0 ? 600 : 400 }}
              >
                {node.url ? (
                  <Link component={RouterLink} to={node.url}>
                    {node.title}
                  </Link>
                ) : (
                  node.title
                )}
              </Typography>
              {node.detail && node.detail.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {formatDetail(node.detail)}
                </Typography>
              )}
            </Box>
          </Stack>
          {node.children.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <ProposalTree
                nodes={node.children}
                depth={depth + 1}
                kindLabel={kindLabel}
                formatDetail={formatDetail}
              />
            </Box>
          )}
        </Box>
      ))}
    </Stack>
  );
}

function ProposalViewInner({
  view,
  onSend,
  busy,
  answeredWith,
}: {
  view: TemplateProposalView;
  onSend: AgentViewComponentProps["onSend"];
  busy: boolean;
  answeredWith: AgentViewComponentProps["answeredWith"];
}) {
  const { t } = useTranslate(["Assistant"]);
  const kindLabel = useTemplateNodeKindLabel();
  const formatDetail = useNodeDetailListFormatter();

  const confirm = () => {
    onSend(
      t("Yes, create the template exactly as proposed.", { ns: "Assistant" }),
      {
        confirm: true,
      }
    );
  };

  return (
    <TemplateViewFrame
      title={view.title}
      description={view.description}
      renderBody={() => (
        <Stack spacing={1}>
          <ProposalTree
            nodes={view.tree}
            depth={0}
            kindLabel={kindLabel}
            formatDetail={formatDetail}
          />
          {view.assumptions.length > 0 && (
            <Alert severity="info" sx={{ py: 0 }}>
              <Typography
                variant="caption"
                component="div"
                sx={{ fontWeight: 600 }}
              >
                {t("Assumptions – please check", { ns: "Assistant" })}
              </Typography>
              {view.assumptions.map((assumption, index) => (
                <Typography key={index} variant="caption" component="div">
                  • {assumption}
                </Typography>
              ))}
            </Alert>
          )}
          <Box>
            <Button
              variant="contained"
              size="small"
              onClick={confirm}
              disabled={busy || answeredWith !== undefined}
            >
              {t("Create as proposed", { ns: "Assistant" })}
            </Button>
          </Box>
        </Stack>
      )}
    />
  );
}

export function ProposalView({
  view,
  onSend,
  busy,
  answeredWith,
}: AgentViewComponentProps) {
  const parsed = templateProposalViewSchema.safeParse(view);
  if (!parsed.success) return null;
  return (
    <ProposalViewInner
      view={parsed.data}
      onSend={onSend}
      busy={busy}
      answeredWith={answeredWith}
    />
  );
}
